import { getTag, useTranslation } from '@msdk/common'
import {
  FieldType,
  FormListFieldColumnDefinition,
  FormOpenMode,
  ListFieldDefinition,
  ListValue,
  traverseSiblings,
} from '@msdk/definitions'
import {
  Box,
  ButtonIcon,
  ButtonSecondary,
  IconSelector,
  InputBaseComponentProps,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHeader,
  TableRow,
} from '@msdk/ui'
import React, { ReactNode, useMemo } from 'react'

import { ListInputProps } from '../../../models/form/list-input-props'
import { ConditionalFieldRenderer, ConditionalFieldRendererBehaviourProps } from '../ConditionalFieldRenderer'
import { shouldShowCellLabel, shouldShowHeader, shouldShowHeaderCell } from './utils'

const StyledTableContainer = styled(TableContainer)`
  border: 0;

  .MuiTableRow-root {
    &:hover {
      background-color: transparent;
    }
  }
`

const StyledTableCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== '$width' })<{ $width?: string }>`
  padding: 16px !important;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.21);

  ${(props) => props.$width && `width: ${props.$width}`}

  .MuiOutlinedInput-root {
    fieldset {
      border-width: 0;
      border-radius: 0;
    }

    legend {
      background-color: #fff;
    }
  }

  .ConditionalField-renderer {
    padding-bottom: 0;
  }

  /* Fix: reset cursor on cell, restore it only on interactive elements */
  cursor: default;

  .MuiFormControlLabel-root {
    width: fit-content;
  }

  .MuiFormControlLabel-root .MuiButtonBase-root,
  .MuiFormControlLabel-root .MuiFormControlLabel-label {
    cursor: pointer;
  }
`

const EmptyHeaderCell = styled(TableCell)`
  height: 15px;
  padding: 0 !important;
  border-bottom: 0 !important;
  box-shadow: none;
`

const ButtonBox = styled(Box)`
  width: 100%;
`

const StyledButton = styled(ButtonSecondary)`
  margin: 16px;
`

export const ListTable = React.forwardRef<HTMLInputElement, InputBaseComponentProps & ListInputProps>(
  function ListTable(props: Omit<ListInputProps, 'ref'>, ref) {
    const t = useTranslation()

    const listValue = props.node.value.data as ListValue
    const rowcount = listValue?.value?.length ?? 0
    const fieldDef = props.node.value.fieldDefinition as ListFieldDefinition

    // Returns true if the header should be shown, false otherwise

    const showHeader = shouldShowHeader(
      props.fieldDef.hideHeader,
      props.formDef.columns,
      props.formDef.defaultColumnStyle?.labelMode,
    )

    const visibleColumns = useMemo(() => {
      if (!props.formDef.columns) {
        return undefined
      }
      return new Set(props.formDef.columns.map((column) => column.name))
    }, [props.formDef.columns])

    const columnDefByName = useMemo(() => {
      const map = new Map<string, FormListFieldColumnDefinition>()
      ;(props.formDef.columns || []).forEach((column) => {
        map.set(column.name, column)
      })
      return map
    }, [props.formDef.columns])

    const canDeleteRow =
      props.fieldDef.canDelete &&
      props.fieldArgs?.formOpenMode !== FormOpenMode.Readonly &&
      props.node.value.state !== 'Readonly'

    const rowsRenderKey = (() => {
      const chunks: string[] = []
      traverseSiblings(props.node.firstChild, (rowNode) => {
        chunks.push(rowNode.id)
        traverseSiblings(rowNode.firstChild, (cellNode) => {
          chunks.push(
            `${cellNode.id}:${cellNode.value.state}:${cellNode.value.visibility}:${
              cellNode.value.validationState
            }:${cellNode.value.data?.toStringForView()}`,
          )
        })
      })
      return chunks.join('|')
    })()

    const rows = useMemo(() => {
      const builtRows: ReactNode[] = []

      if (props.node.firstChild) {
        traverseSiblings(props.node.firstChild, (rowNode) => {
          const cells: ReactNode[] = []

          traverseSiblings(rowNode.firstChild, (cellNode) => {
            if (!visibleColumns || visibleColumns.has(cellNode.id)) {
              const colDef = columnDefByName.get(cellNode.id) ?? props.formDef.defaultColumnStyle
              const showLabel = shouldShowCellLabel(colDef, props.formDef.defaultColumnStyle)
              const behaviour: ConditionalFieldRendererBehaviourProps =
                props.fieldArgs?.formOpenMode === FormOpenMode.Readonly
                  ? { readonly: true }
                  : { onChange: (e) => props.onFieldChange(e, rowNode.id) }

              cells.push(
                <StyledTableCell key={cellNode.id} size="small" $width={colDef?.width}>
                  <ConditionalFieldRenderer
                    {...behaviour}
                    node={cellNode}
                    type={cellNode.value.type as FieldType}
                    key={cellNode.id}
                    showLabel={showLabel}
                    onReload={props.fieldArgs?.onReload}
                  />
                </StyledTableCell>,
              )
            }
          })

          if (canDeleteRow) {
            cells.push(
              <StyledTableCell width="72px" key={rowNode.id + '_DEL'} scope="row" size="small">
                <ButtonIcon onClick={() => props.onRowDelete(rowNode.id)} color="primary">
                  <IconSelector iconName="Close" />
                </ButtonIcon>
              </StyledTableCell>,
            )
          }

          builtRows.push(<TableRow key={rowNode.id}>{cells}</TableRow>)
        })
      }

      return builtRows
    }, [
      canDeleteRow,
      columnDefByName,
      props.fieldArgs?.formOpenMode,
      props.formDef.defaultColumnStyle,
      props.node.firstChild,
      props.onFieldChange,
      props.onRowDelete,
      rowsRenderKey,
      visibleColumns,
    ])

    const header = useMemo(() => {
      const cells = props.fieldDef.fields.map((field) => {
        if (!visibleColumns || visibleColumns.has(field.name)) {
          const colDef = columnDefByName.get(field.name)

          if (shouldShowHeaderCell(colDef, props.formDef.defaultColumnStyle)) {
            return (
              <StyledTableCell key={field.name} scope="col" size="small">
                {t(field.idml, field.description)}
              </StyledTableCell>
            )
          }
          return <StyledTableCell width="72px" scope="col" size="small" key={field.name} />
        }

        return null
      })

      if (canDeleteRow) {
        cells.push(<StyledTableCell width="72px" scope="col" size="small" key="empty-col-header" />)
      }

      return cells
    }, [canDeleteRow, columnDefByName, props.fieldDef.fields, props.formDef.defaultColumnStyle, t, visibleColumns])

    const isReadOnly = props.node.value.state === 'Readonly'

    return (
      <ButtonBox>
        {props.fieldDef.canAdd && !isReadOnly && (
          <StyledButton
            onClick={props.onRowAdd}
            data-test-id={getTag(props.moduleName, props.fieldDef.name, 'add', 'button')}
            startIcon={<IconSelector iconName="Add" />}
            disabled={fieldDef.maxRows !== -1 && rowcount >= fieldDef.maxRows}
          >
            {t('CMPSR_LISTFIELD_ADD', 'Add')}
          </StyledButton>
        )}
        <StyledTableContainer ref={ref}>
          <Table>
            {showHeader && (
              <TableHeader>
                <TableRow>{header}</TableRow>
              </TableHeader>
            )}
            {!showHeader && (
              <TableHeader>
                <TableRow>
                  <EmptyHeaderCell colSpan={1000} size="small" />
                </TableRow>
              </TableHeader>
            )}
            <TableBody>{rows}</TableBody>
          </Table>
        </StyledTableContainer>
      </ButtonBox>
    )
  },
)
